/* @flow */
import { IntlShape } from 'react-intl';
import { uniqBy } from 'ramda';

import messages from 'src/shared/domain/user/l10n/userL10n';
import { userCanConnectOnInterests } from '../../connect-on-interests/util/connectOnInterestsUtil';

import { getLocalizedData } from '@braindate/domain/lib/base/l10n/util';
import {
  getUserLifeTitles,
  getUserDreamBraindate,
  getUserInterests,
  getUserPosition,
  getUserCompany,
  getUserLoveTalking,
  getUserWantLearn,
  getUserKnowledge,
} from '@braindate/domain/lib/user/util';

import type { User } from '@braindate/domain/lib/user/type';
import type { Topic } from '@braindate/domain/lib/topic/type';
import type { Keyword } from 'src/shared/domain/keyword/type/keywordTypes';
import { getKeywordLabel } from 'src/shared/domain/keyword/util/keywordUtils';

export const usersCantMeetModalId = 'users-cant-meet-modal';

export function isUserProfileEmpty(user: User, locale: string): boolean {
  const roles = getUserLifeTitles(user, locale);
  const dream = getUserDreamBraindate(user, locale);

  const interests = getUserInterests(user) || [];
  const canConnectOnInterests = userCanConnectOnInterests(user, locale);

  return !(roles || dream || interests.length > 0 || canConnectOnInterests);
}

export function getUserTitle(user: User, intl: IntlShape) {
  let title = null;

  const position = getUserPosition(user);
  const company = getUserCompany(user);

  if (position && company) {
    title = intl.formatMessage(messages.fullPosition, {
      position,
      company,
    });
  } else {
    title = position || company;
  }

  return title;
}

export function canUsersCanMeet(
  self: User,
  user: User,
  selfTopics: Topic[],
  userTopics: Topic[],
  connectOnInterestsEnabled: boolean,
  locale: string,
): boolean {
  const selfHasTopics = !!selfTopics.length;
  const userHasTopics = !!userTopics.length;

  const userHasInterest = connectOnInterestsEnabled
    ? userCanConnectOnInterests(user, locale)
    : true;

  if (userHasInterest || selfHasTopics || userHasTopics) return true;

  return false;
}

export function getUserKeywords(
  user: User,
  locale: string,
): Array<Keyword> | null {
  const userBody = getLocalizedData(user, locale);
  if (!userBody) return null;
  if (
    ['love_talking_about', 'knowledgeable_about', 'want_to_learn'].every(
      (key) => typeof userBody[key] === 'undefined',
    )
  ) {
    return null;
  }

  const loveTalkingAbout = getUserLoveTalking(user, locale) || [];
  const wantToLearn = getUserWantLearn(user, locale) || [];
  const knowledge = getUserKnowledge(user, locale) || [];

  return uniqBy(getKeywordLabel, [
    ...loveTalkingAbout,
    ...wantToLearn,
    ...knowledge,
  ]);
}
